import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '@/components/sections/Header';
import Footer from '@/components/sections/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import OgMeta from '@/components/OgMeta';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import procedimentos from '@/data/procedimentos.json';

/**
 * Mapa do site em HTML: todas as páginas públicas numa página só.
 *
 * Procedimentos vêm de src/data/procedimentos.json (a mesma lista do menu, do
 * rodapé e do sitemap.xml) e os artigos da mesma API do /blog. O prerender
 * falha o build se alguma URL do sitemap.xml não aparecer aqui — então página
 * nova que entrar no sitemap sem entrar nesta lista quebra o deploy, não some.
 */
const TITLE = 'Mapa do Site | Dr. Luis Brunello — Cirurgia Plástica';
const DESCRIPTION =
  'Todas as páginas do site do Dr. Luis Brunello, cirurgião plástico em Curitiba: procedimentos, artigos do blog, glossário e páginas institucionais.';

const INSTITUCIONAL = [
  { name: 'Início', to: '/' },
  { name: 'Sobre o Dr. Luis Brunello', to: '/sobre' },
  { name: 'Jornada do Paciente', to: '/jornada' },
  { name: 'Contato', to: '/contato' },
  { name: 'Blog', to: '/blog' },
  { name: 'Glossário', to: '/glossario' },
  { name: 'Mapa do site', to: '/mapa-do-site' },
];

const LEGAL = [
  { name: 'Política de Privacidade', to: '/politica-de-privacidade' },
  { name: 'Termos de Uso', to: '/termos-de-uso' },
];

const Group: React.FC<{ title: string; items: { name: string; to: string }[] }> = ({ title, items }) => (
  <section className="mb-10">
    <h2 className="text-lg text-gold font-medium mb-4 pb-2 border-b border-white/5">{title}</h2>
    <ul className="space-y-2 text-sm">
      {items.map(({ name, to }) => (
        <li key={to}>
          <Link to={to} className="text-light-secondary hover:text-gold transition-colors">{name}</Link>
        </li>
      ))}
    </ul>
  </section>
);

const MapaDoSitePage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { posts } = useBlogPosts();

  return (
    <div className="min-h-screen bg-dark-base">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://luisbrunello.com.br/mapa-do-site" />
      </Helmet>
      <OgMeta title={TITLE} description={DESCRIPTION} path="/mapa-do-site" />

      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} scrollToSection={() => {}} onOpenBooking={() => {}} />

      <main className="pt-28 pb-20">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Mapa do site' }]} className="mb-10" />

          <header className="mb-12">
            <h1 className="text-3xl md:text-4xl text-light-primary tracking-tight leading-tight mb-4">Mapa do site</h1>
            <p className="text-light-muted text-sm leading-relaxed">
              Todas as páginas publicadas neste site, agrupadas por assunto.
            </p>
          </header>

          <Group title="Institucional" items={INSTITUCIONAL} />

          {procedimentos.map((g) => (
            <Group
              key={g.label}
              title={`Procedimentos — ${g.label}`}
              items={g.items.map((p) => ({ name: p.name, to: `/procedimentos/${p.slug}` }))}
            />
          ))}

          {posts.length > 0 && (
            <Group title="Artigos do blog" items={posts.map((p) => ({ name: p.title, to: `/blog/${p.slug}` }))} />
          )}

          <Group title="Páginas legais" items={LEGAL} />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MapaDoSitePage;
